import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "@/store/use-auth-store";
import { useCreateCheckoutSession } from "@/api/hooks/use-subscription";
import { usePricing } from "@/api/hooks/use-pricing";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";

const PRICE_ID_STORAGE_KEY = "pending_stripe_price_id";
const LOGIN_REDIRECT_DONE_KEY = "price_id_login_redirect_done";

export const PriceIdCheckoutHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { createCheckoutSession } = useCreateCheckoutSession();
  const { info } = useToast();
  const { t } = useTranslation();
  const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if (!pendingPriceId) return;
    if (isAuthenticated()) {
      if (location.pathname !== "/") navigate("/", { replace: true });
      return;
    }
    if (sessionStorage.getItem(LOGIN_REDIRECT_DONE_KEY)) return;
    sessionStorage.setItem(LOGIN_REDIRECT_DONE_KEY, "1");
    navigate("/login", { replace: true });
  }, [pendingPriceId, isAuthenticated, location.pathname, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const priceId = searchParams.get("priceId");
    if (!priceId) return;

    localStorage.setItem(PRICE_ID_STORAGE_KEY, priceId);
    // If the user is already on a specific page (not root), they navigated here
    // intentionally — don't redirect them away to login.
    if (location.pathname !== "/") {
      sessionStorage.setItem(LOGIN_REDIRECT_DONE_KEY, "1");
    }
    setPendingPriceId(priceId);

    searchParams.delete("priceId");
    const nextSearch = searchParams.toString();
    const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;

    if (nextUrl !== `${location.pathname}${location.search}`) {
      navigate(nextUrl, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (pendingPriceId) return;
    const storedPriceId = localStorage.getItem(PRICE_ID_STORAGE_KEY);
    if (storedPriceId) {
      setPendingPriceId(storedPriceId);
    }
  }, [pendingPriceId]);

  const { data: pricingData, isSuccess: isPricingReady } = usePricing({
    enabled: !!pendingPriceId,
  });

  useEffect(() => {
    if (!pendingPriceId) return;
    if (!isAuthenticated() || !user?._id || !user.email) return;
    if (location.pathname !== "/") return;
    if (!isPricingReady) return;
    if (isProcessingRef.current) return;

    const matchedPlan = pricingData?.data?.result?.find(
      (plan) => plan.stripePriceId === pendingPriceId
    );

    if (!matchedPlan) {
      console.warn("Stripe priceId ile eşleşen plan bulunamadı:", pendingPriceId);
      localStorage.removeItem(PRICE_ID_STORAGE_KEY);
      sessionStorage.removeItem(LOGIN_REDIRECT_DONE_KEY);
      setPendingPriceId(null);
      return;
    }

    isProcessingRef.current = true;
    info(t("payment.checkoutStartingMessage"), {
      title: t("payment.checkoutStartingTitle"),
      duration: 4000,
    });

    const startCheckout = async () => {
      try {
        const session = await createCheckoutSession({ planId: matchedPlan._id });

        if (session?.data?.url) {
          localStorage.removeItem(PRICE_ID_STORAGE_KEY);
          sessionStorage.removeItem(LOGIN_REDIRECT_DONE_KEY);
          setPendingPriceId(null);
          window.location.href = session.data.url;
          return;
        }

        console.warn("Checkout session URL alınamadı:", session);
      } catch (sessionError) {
        console.log('sessionError',sessionError)
        const statusCode =
          (sessionError as { response?: { status?: number } })?.response
            ?.status ??
          (sessionError as { status?: number })?.status;

        if (statusCode === 400 || statusCode === 500) {
          localStorage.removeItem(PRICE_ID_STORAGE_KEY);
          sessionStorage.removeItem(LOGIN_REDIRECT_DONE_KEY);
          setPendingPriceId(null);
        }

        console.error("Checkout session başlatılamadı:", sessionError);
      } finally {
        isProcessingRef.current = false;
      }
    };

    startCheckout();
  }, [
    pendingPriceId,
    isAuthenticated,
    user?._id,
    user?.email,
    location.pathname,
    isPricingReady,
    pricingData,
    createCheckoutSession,
  ]);

  return null;
};
